import { MoreHoriz } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from '@mui/material';
import { CalculsFormListeProjection } from '@norma/shared/constants/calculs';
import { fCurrency } from '@norma/shared/helpers/formatNumber';
import { Bareme } from '@norma/shared/types/bareme.type';
import {
  LIBELLE_PGPF_RELIQUAT,
  ListeProjectionIncidenceProfessionelle,
  ListeProjectionIncidenceProfessionelleRow,
  NumeroPieceValuesRow,
  PerteDeRetraiteValues,
  PGPFReliquatAndPGPFReliquatActivationStatus,
  PrejudiceFormListeProjection,
} from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import i18next from 'i18next';
import { intersection } from 'lodash';
import React, { useMemo, useState } from 'react';
import {
  Control,
  FieldPath,
  useFormContext,
  useFormState,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CalculsBox } from 'src/components/basic/CalculsBox';
import { ComputedPropsForm } from 'src/components/forms/ComputedPropsForm';
import { ExpandableTextFieldForm } from 'src/components/forms/ExpandableTextFieldForm';
import {
  validationSchemaCapitalisation,
  validationSchemaNumeroPieces,
} from 'src/constants/prejudice/validation';
import { fPartResponsabilite } from 'src/helpers/formatValues';
import { dateString } from 'src/helpers/yup';
import { useFormArrayUtils } from 'src/hooks/useFormArrayUtils';
import * as yup from 'yup';
import { ComputedReadFieldForm } from '../../../forms/ComputedReadFieldForm';
import { TextFieldForm } from '../../../forms/TextFieldForm';
import { IMaskNumberProps, MaskNumber } from '../../../masks/MaskNumber';
import { NormalTable } from '../../../styled';
import { PrejudiceTab } from '../../PrejudiceTab';
import { FormNumeroPieceDialog } from '../FormNumeroPieceDialog';
import { TotalIndemniteGlobaleARepartirAEchoir } from '../TotalIndemniteGlobaleARepartirAEchoir';
import { PerteDeRetraite } from './PerteDeRetraite';

export const incidenceProfessionnellePGPFReliquatRow = (
  PGPFReliquat: number | null,
): ListeProjectionIncidenceProfessionelleRow => ({
  libelle: LIBELLE_PGPF_RELIQUAT,
  montant: PGPFReliquat || 0,
  numerosPieces: { rows: [] },
});

interface Props {
  control: Control<PrejudiceFormListeProjection>;
  PGPFReliquat: PGPFReliquatAndPGPFReliquatActivationStatus;
  partResponsabilite: number;
  procedure: Procedure;
  dateLiquidation: Date | null | undefined;
  baremes: Bareme[];
  dateNaissance: Date | null | undefined;
  dateDeces: Date | null | undefined;
  sexe: 'm' | 'f' | 'u';
  allNumerosPieces: NumeroPieceValuesRow[];
  PGPFViager: boolean;
}

const validationSchemaPerteDeRetraite = (
  birthYear: number,
  dateLiquidation: Date | undefined,
): yup.ObjectSchema<PerteDeRetraiteValues> =>
  yup.object({
    retraite: yup.object({
      anneeRetraite: yup.number().nullable().defined().min(birthYear),
      retraiteTheoriqueTotal: yup.number().required(),
      retraitePercue: yup.number().required(),
      perteAnnuelle: yup.number().required(),
    }),
    echus: yup.object({
      nombreAnneesEntreAnneeRetraiteEtAnneeCalcul: yup.number().required(),
      montantAPayerADateCalcul: yup.number().required(),
    }),
    aEchoir: validationSchemaCapitalisation.shape({
      sommeACapitaliser: yup.number().required(),
      dateCapitalisation: dateString()
        .nullable()
        .defined()
        .when([], (_, schema) =>
          dateLiquidation
            ? schema.minDate(
                undefined,
                i18next.t('validation.prejudices.dates.minDateLiquidation'),
                dateLiquidation,
              )
            : schema,
        ),
    }),
    numerosPieces: validationSchemaNumeroPieces,
  });
export const validationSchemaIncidenceProfessionnelle = (
  birthYear: number,
  dateLiquidation: Date | undefined,
): yup.ObjectSchema<ListeProjectionIncidenceProfessionelle> =>
  yup.object({
    rows: yup
      .array()
      .defined()
      .of(
        yup.object({
          numerosPieces: validationSchemaNumeroPieces,
          libelle: yup.string().required(),
          montant: yup.number().required(),
        }),
      ),
    perteDeRetraite: validationSchemaPerteDeRetraite(
      birthYear,
      dateLiquidation,
    ),
  });
const initialValuesRow: ListeProjectionIncidenceProfessionelleRow = {
  libelle: '',
  montant: 0,
  numerosPieces: { rows: [] },
};

export const IncidenceProfessionnelle: React.FC<Props> = ({
  control,
  PGPFReliquat,
  partResponsabilite,
  procedure,
  dateLiquidation,
  baremes,
  dateNaissance,
  dateDeces,
  sexe,
  allNumerosPieces,
  PGPFViager,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  const { getValues, setValue } = useFormContext();

  const { duplicate, remove, fields, append } = useFormArrayUtils({
    control,
    name: 'prejudiceValues.rows',
    getValues,
    setValue,
  });

  const handleMenuOpen =
    (index: number) => (event: React.MouseEvent<HTMLElement>) => {
      setOpenMenuIndex(index);
      setAnchorEl(event.currentTarget);
    };

  const handleMenuClose = () => {
    setOpenMenuIndex(null);
    setAnchorEl(null);
  };

  const handleDelete = (index: number) => {
    remove(index);
    handleMenuClose();
  };

  const handleDuplicate = (index: number) => {
    duplicate(index);
    handleMenuClose();
  };

  const { errors } = useFormState({ control });
  const [tabIndex, setTabIndex] = useState(0);

  const isFirstTabError = useMemo(() => {
    const firstTabFields: FieldPath<ListeProjectionIncidenceProfessionelle>[] =
      ['rows'];
    return (
      intersection(Object.keys(errors.prejudiceValues || {}), firstTabFields)
        .length > 0
    );
  }, [errors]);
  const isSecondTabError = useMemo(() => {
    const secondTabFields: FieldPath<ListeProjectionIncidenceProfessionelle>[] =
      ['perteDeRetraite'];
    return (
      intersection(Object.keys(errors.prejudiceValues || {}), secondTabFields)
        .length > 0
    );
  }, [errors]);

  return (
    <>
      <Tabs value={tabIndex} onChange={(_, newValue) => setTabIndex(newValue)}>
        <PrejudiceTab
          label={t(
            'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.tabs.incidenceProfessionnelle',
          )}
          value={0}
          isError={isFirstTabError}
        />
        {!PGPFViager && (
          <PrejudiceTab
            label={t(
              'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.tabs.perteDeRetraite',
            )}
            value={1}
            isError={isSecondTabError}
          />
        )}
        <PrejudiceTab
          label={t(
            'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.tabs.recapitulatif',
          )}
          value={2}
        />
      </Tabs>

      <Stack
        sx={{ display: tabIndex === 0 ? undefined : 'none' }}
        alignItems="center"
      >
        <TableContainer component={Paper}>
          <NormalTable size="medium">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t(
                    'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.fields.numerosPieces.columnHeader',
                  )}
                </TableCell>
                <TableCell align="center">
                  {t(
                    'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.fields.libelle.columnHeader',
                  )}
                </TableCell>
                <TableCell align="center">
                  {t(
                    'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.fields.montant.columnHeader',
                  )}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => (
                <TableRow key={field.id}>
                  <TableCell>
                    <FormNumeroPieceDialog
                      control={control}
                      name={`prejudiceValues.rows.${index}.numerosPieces`}
                      allOtherNumeroPieces={allNumerosPieces}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <ExpandableTextFieldForm
                      control={control}
                      name={`prejudiceValues.rows.${index}.libelle`}
                      sx={{ minWidth: 300 }}
                      dialogTitle={t(
                        'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.fields.libelle.columnHeader',
                      )}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextFieldForm
                      control={control}
                      name={`prejudiceValues.rows.${index}.montant`}
                      InputProps={{
                        inputComponent: MaskNumber as any,
                        inputProps: {
                          numberMask: { scale: 2, signed: true },
                          suffix: '€',
                        } as IMaskNumberProps,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton onClick={handleMenuOpen(index)}>
                      <MoreHoriz />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={openMenuIndex === index}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={() => handleDelete(index)}>
                        {t('forms.table.deleteRow', { index: index + 1 })}
                      </MenuItem>
                      <MenuItem onClick={() => handleDuplicate(index)}>
                        {t('forms.table.duplicateRow', { index: index + 1 })}
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </NormalTable>
        </TableContainer>
        <Button
          sx={{ alignSelf: 'center' }}
          onClick={() => {
            append(initialValuesRow);
          }}
        >
          {t(
            'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.addRow',
          )}
        </Button>
      </Stack>
      <Box sx={{ display: tabIndex === 1 ? undefined : 'none' }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            maxWidth: 200,
            marginBottom: 2,
          }}
        >
          <FormNumeroPieceDialog
            control={control}
            name="prejudiceValues.perteDeRetraite.numerosPieces"
            allOtherNumeroPieces={allNumerosPieces}
          />
        </Box>
        <PerteDeRetraite
          control={control}
          procedure={procedure}
          dateLiquidation={dateLiquidation}
          baremes={baremes}
          dateNaissance={dateNaissance}
          dateDeces={dateDeces}
          sexe={sexe}
        />
      </Box>
      <Box sx={{ display: tabIndex === 2 ? undefined : 'none' }}>
        <ComputedPropsForm
          control={control}
          watchFields={[
            'prejudiceValues.rows',
            'prejudiceValues.perteDeRetraite',
          ]}
          compute={([rows, perteDeRetraite]) => ({
            props:
              CalculsFormListeProjection.getIncidenceProfessionnelleIndemnite({
                partResponsabilite,
                PGPFReliquat,
                rows: rows as ListeProjectionIncidenceProfessionelleRow[],
                perteDeRetraite,
              }),
          })}
          render={(values) => {
            const { montantTotal, beforePGPFReliquatTotal } =
              values as ReturnType<
                typeof CalculsFormListeProjection.getIncidenceProfessionnelleIndemnite
              >;
            return (
              <Box sx={{ marginTop: 3, marginBottom: 2 }}>
                <ComputedReadFieldForm
                  name="montantTotal"
                  control={control}
                  watchFields={[]}
                  customValues={[montantTotal]}
                  compute={(_, customValues) => customValues?.[0] || 0}
                />
                {PGPFReliquat.PGPFReliquat &&
                PGPFReliquat.activatePGPFReliquat ? (
                  <CalculsBox>
                    <NormalTable>
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell align="center">
                            {t(
                              'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.PGPFReliquat.totalTable.fields.incidenceProfessionnelle.columnHeader',
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {t(
                              'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.PGPFReliquat.totalTable.fields.tiersPayeurs.columnHeader',
                            )}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell align="left" variant="head">
                            {t(
                              'prejudice.prejudicesFormTypes.LISTE_PROJECTION.INCIDENCE_PROFESSIONNELLE.form.PGPFReliquat.totalTable.totalRowHeader',
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {beforePGPFReliquatTotal &&
                              fCurrency(beforePGPFReliquatTotal)}
                          </TableCell>
                          <TableCell align="center">
                            {fCurrency(-PGPFReliquat.PGPFReliquat)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell align="left" variant="head">
                            {t(`partResponsabilite.tableRowHeader`, {
                              partResponsabilite: fPartResponsabilite(
                                partResponsabilite * 100,
                              ),
                            })}
                          </TableCell>
                          <TableCell align="center">
                            {beforePGPFReliquatTotal &&
                              fCurrency(
                                beforePGPFReliquatTotal * partResponsabilite,
                              )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </NormalTable>
                  </CalculsBox>
                ) : null}
              </Box>
            );
          }}
        />
        <ComputedPropsForm
          control={control}
          watchFields={['prejudiceValues']}
          compute={([prejudiceValues]) => ({
            props: {
              indemniteRepartie:
                CalculsFormListeProjection.getIncidenceProfessionnelleIndemniteRepartie(
                  {
                    partResponsabilite,
                    incidenceProfessionnelle:
                      prejudiceValues as ListeProjectionIncidenceProfessionelle,
                    PGPFReliquat,
                  },
                ),
            },
          })}
          render={(props) => {
            const indemniteRepartie = props.indemniteRepartie as ReturnType<
              typeof CalculsFormListeProjection.getIncidenceProfessionnelleIndemniteRepartie
            >;
            return (
              <TotalIndemniteGlobaleARepartirAEchoir
                indemniteGlobaleARepartir={indemniteRepartie}
                hide={{
                  indemniteTiersPayeurs: {
                    arreragesEchus: true,
                    arreragesAEchoir: true,
                  },
                }}
                partResponsabilite={partResponsabilite}
              />
            );
          }}
        />
      </Box>
    </>
  );
};
